import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, UsersRound, Loader2, ShieldAlert } from "lucide-react";

// Public claim page for the "one-tap" team magic link.
// URL: /join?token=<jwt>. If the visitor is already logged in with the matching email,
// we auto-claim on load. Otherwise they can sign in or register (with email pre-filled).
export default function JoinPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { user, login, register, refresh } = useAuth();

  const [info, setInfo] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [mode, setMode] = useState("register");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [hipaaAck, setHipaaAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [claiming, setClaiming] = useState(false);

  // Load invite info
  useEffect(() => {
    if (!token) { setLoadErr("Missing invite token in link."); return; }
    http.get(`/team/magic-info?token=${encodeURIComponent(token)}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setLoadErr(formatApiError(e.response?.data?.detail) || "This invite link is invalid or expired."));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only token change should refetch; setters are stable.
  }, [token]);

  // Auto-claim if already logged in with the matching email
  useEffect(() => {
    if (!info || !user || claiming) return;
    if ((user.email || "").toLowerCase() !== (info.invitee_email || "").toLowerCase()) return;
    (async () => {
      setClaiming(true);
      try {
        await http.post("/team/magic-claim", { token });
        toast.success(`Joined ${info.clinic_name || info.team_owner_name}'s team`);
        await refresh?.();
        nav("/learners");
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not join team");
      } finally {
        setClaiming(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- guarded by `claiming` sentinel; re-adding it would cause immediate re-runs.
  }, [info, user, token, nav, refresh]);

  const submit = async (e) => {
    e.preventDefault();
    if (!info) return;
    if (mode === "register" && !hipaaAck) {
      toast.error("You must acknowledge the HIPAA notice to create an account.");
      return;
    }
    setBusy(true);
    const ok = mode === "login"
      ? await login(info.invitee_email, password)
      : await register(info.invitee_email, password, name.trim(), hipaaAck);
    if (!ok) { setBusy(false); return; }
    // Claim right after auth
    try {
      await http.post("/team/magic-claim", { token });
      toast.success(`Joined ${info.clinic_name || info.team_owner_name}'s team`);
      await refresh?.();
      nav("/learners");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Signed in but could not claim invite");
      nav("/learners");
    } finally {
      setBusy(false);
    }
  };

  if (loadErr) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
        <div className="max-w-md w-full rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="join-error">
          <ShieldAlert className="w-8 h-8 text-rose-500 mx-auto" />
          <div className="font-display font-extrabold text-xl mt-3">Invite unavailable</div>
          <div className="text-sm text-slate-500 mt-2">{loadErr}</div>
          <Link to="/auth" className="inline-block mt-6 text-sm font-semibold text-slate-900 underline">Go to sign-in</Link>
        </div>
      </div>
    );
  }
  if (!info) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading invite…
      </div>
    );
  }

  const emailMismatch = user && (user.email || "").toLowerCase() !== (info.invitee_email || "").toLowerCase();
  const teamLabel = info.clinic_name || info.team_owner_name || "the team";

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-8 bg-slate-50">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="join-card">
        <div className="flex items-center gap-2">
          <img src="/icon-192.png" alt="Discrimina" className="w-9 h-9 rounded-xl" />
          <div className="font-display font-extrabold text-lg">Discrimina</div>
        </div>
        <div className="mt-6 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
          <UsersRound className="w-3.5 h-3.5" /> Team invite
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold mt-1 leading-tight">
          Join {teamLabel}
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          <strong>{info.team_owner_name}</strong> invited <span className="font-semibold text-slate-800">{info.invitee_email}</span> as an <em>{info.role}</em>.
        </p>

        {user && !emailMismatch ? (
          <div className="mt-6 rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700" data-testid="join-auto">
            {claiming ? (
              <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Joining team…</span>
            ) : (
              <>You're signed in. Redirecting to your new workspace…</>
            )}
          </div>
        ) : emailMismatch ? (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="join-email-mismatch">
            You're signed in as <strong>{user.email}</strong>, but this invite is for <strong>{info.invitee_email}</strong>.
            Log out and sign back in with the invited email to accept.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="join-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-bold text-slate-500">Email</Label>
              <Input value={info.invitee_email} disabled className="mt-1.5 h-11 rounded-xl bg-slate-100" data-testid="join-email-readonly" />
            </div>

            <div className="flex gap-2 text-xs">
              <button type="button" onClick={() => setMode("register")} data-testid="join-mode-register"
                className={`flex-1 h-9 rounded-full font-semibold border transition-colors ${mode === "register" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
                Create account
              </button>
              <button type="button" onClick={() => setMode("login")} data-testid="join-mode-login"
                className={`flex-1 h-9 rounded-full font-semibold border transition-colors ${mode === "login" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
                I have one
              </button>
            </div>

            {mode === "register" && (
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] font-bold text-slate-500">Your name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required
                  className="mt-1.5 h-11 rounded-xl" data-testid="join-name-input" />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-bold text-slate-500">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                minLength={mode === "register" ? 10 : 6}
                className="mt-1.5 h-11 rounded-xl" data-testid="join-password-input" />
              {mode === "register" && (
                <div className="mt-1.5 text-[11px] text-slate-500">Min 10 chars, mixed case + a digit</div>
              )}
            </div>

            {mode === "register" && (
              <label className="flex items-start gap-3 text-xs text-slate-600 cursor-pointer">
                <Checkbox checked={hipaaAck} onCheckedChange={(v) => setHipaaAck(!!v)} className="mt-0.5" data-testid="join-hipaa-checkbox" />
                <span>
                  I understand Discrimina provides HIPAA <em>technical</em> safeguards only. See the {" "}
                  <a href="/compliance" className="underline font-semibold" target="_blank" rel="noreferrer">Compliance page</a>.
                </span>
              </label>
            )}

            <Button type="submit" disabled={busy} className="w-full h-11 rounded-full font-semibold" data-testid="join-submit-button">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Joining…</> : `Accept invite & continue`}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
